import { useOutlookActions } from "@/pages/taskpane/lib/office/hooks";
import { OrderService } from "@/pages/taskpane/lib/order/service/OrderService";
import {
  ICheckMoveOrderRequest,
  ICheckResourceSuitableForMovingOrderResponse,
  IDialogMessageBase,
  IOpenOrderRequest,
  IRequestMeetingType,
  ISavePlaceOrderSessionDataRequest,
} from "@/pages/taskpane/lib/order/types/OrderEntity";
import {
  DialogErrorCode,
  DialogEventType,
  EOfficeDialogMessageType,
  IDialogModel,
} from "@/pages/taskpane/lib/utils/dialog";
import { usePreSelectedResourceStore } from "@/pages/taskpane/shared/stores/preSelectedResourceStore";
import { EPreSelectedResource } from "@/pages/taskpane/shared/types/enums/preSelectedResourceEnum";
import { PreSelectedResourceInfo } from "@/pages/taskpane/shared/types/preSelectedResource";
import { useMemo } from "react";
import { ETypeOrderResource } from "../services/types/enum";
import { ResourceEntity } from "../services/types/resourceEntity";
import { saveMeetingType } from "../services/useSaveMeetingType";
import { useResourceStore } from "../store/resourceStore";
import { useAttendeesStore } from "@/pages/taskpane/shared/stores";
export const useOrderForm = () => {
  const orderService = useMemo(() => new OrderService(), []);
  const { addItem } = usePreSelectedResourceStore();
  const { saveSessionId } = useOutlookActions();
  const { setMeetingLabel, updateResourceIsOrdered } = useResourceStore();
  const { UpdateAttendees } = useOutlookActions();
  const { removeRequiredAttendee, removeOptionalAttendee } = useAttendeesStore();

  const savePlaceOrderSessionData = async (request: ISavePlaceOrderSessionDataRequest) => {
    const response = await orderService.savePlaceOrderSessionData(request);
    return response;
  };

  const checkResourceSuitableForMovingOrder = async (
    request: ICheckMoveOrderRequest
  ): Promise<ICheckResourceSuitableForMovingOrderResponse> => {
    const response = await orderService.checkResourceSuitableForMovingOrder(request);
    return response;
  };

  const handleUpdateAttendees = (
    attendees: { Email: string; Name: string }[],
    attendeesFromStore: { Email: string; Name: string; IsRequired?: boolean }[]
  ) => {
    const attendeeForAdd = attendees?.filter(
      (attendee) =>
        !attendeesFromStore?.some(
          (a) =>
            a.Email.toLowerCase() === attendee.Email.toLowerCase()
        )
    )
    const attendeeForRemove = attendeesFromStore?.filter(
      (attendee) =>
        !attendees?.some(
          (a) =>
            a.Email.toLowerCase() === attendee.Email.toLowerCase()
        )
    );
    if(!attendeeForAdd.length && !attendeeForRemove.length) {
      return;
    }
    if(attendeeForRemove.length) {
      attendeeForRemove.forEach(attendee => {
        if(attendee.IsRequired) {
          removeRequiredAttendee(attendee.Email);
        } else {
          removeOptionalAttendee(attendee.Email);
        }
      });
    }
    UpdateAttendees(attendeeForAdd, attendeeForRemove);
  };

  const updateIsOrdered = (resource: ResourceEntity, sessionId: string) => {
    saveSessionId(resource.email?.toLowerCase(), `${sessionId}_save_ordered`);
    updateResourceIsOrdered(resource.email?.toLowerCase(), true);
  };

  const openOrder = async (
    resource: ResourceEntity,
    requestSaveSession: ISavePlaceOrderSessionDataRequest,
    request: IOpenOrderRequest,
    requestMeetingType?: IRequestMeetingType
  ) => {
    const saveSessionResponse = await savePlaceOrderSessionData(requestSaveSession);
    if (!saveSessionResponse.Success) {
      return;
    }
    orderService.openOrder(request, (dialog: IDialogModel) => {
      saveSessionId(resource.email?.toLowerCase(), requestSaveSession.SessionId);
      dialog.addEventHandler(
        DialogEventType.DialogEventReceived,
        (args: { message: string; origin: string | undefined } | { error: number }) => {
          if ((args as { error: number }).error === DialogErrorCode.Closed) {
            console.log("Order dialog closed");
          }
        }
      );
      dialog.addEventHandler(
        DialogEventType.DialogMessageReceived,
        (args: { message: string; origin: string | undefined } | { error: number }) => {
          //console.log(args, "Message from child");
          if ((args as { message: string }).message) {
            const message = JSON.parse((args as { message: string }).message) as IDialogMessageBase;
            switch (message.MessageType) {
              case EOfficeDialogMessageType.SavedReservations: {
                updateIsOrdered(resource, `${requestSaveSession.SessionId}_save_ordered`);
                const meetingTypeId = Number(message?.MeetingType?.Value);
                if (
                  meetingTypeId &&
                  meetingTypeId !== requestMeetingType?.meetingLabelSelected &&
                  requestMeetingType?.meetingLabelOptions
                ) {
                  saveMeetingType({
                    uid: request.CustomUid,
                    resourcesIds: request.SelectedResources,
                    // originalResourceIds: selectedResource.map((item) => item.id).join(","),
                    meetingType:
                      requestMeetingType?.meetingLabelOptions.find((item) => item.id === meetingTypeId)?.name || "",
                    isRecurring: request.IsRecurringApp === "1" ? 1 : 0,
                    isNewMeeting: !!requestMeetingType?.isNewMeeting,
                    timeZoneName: request.TimeZoneCode || "",
                    organizerName: request.OrganizerEmail || "",
                    organizerEmail: request.OrganizerEmail || "",
                    startDate: requestMeetingType?.startDate || "",
                    endDate: requestMeetingType?.endDate || "",
                    globalId: request.GlobalId || "",
                    isSendMail: false,
                    subject: request.Subject || "",
                  });
                  setMeetingLabel(meetingTypeId);
                }
                break;
              }
              // eslint-disable-next-line no-case-declarations
              case EOfficeDialogMessageType.CloseDialog: {
                dialog.close();
                break;
              }
              case EOfficeDialogMessageType.UpdateAttendees: {
                updateIsOrdered(resource, `${requestSaveSession.SessionId}_save_ordered`);
                const attendees = (message?.Attendees || []).filter(attendee => attendee.Email.toLowerCase() !== request.OrganizerEmail?.toLowerCase());
                const attendeesFromStore = requestSaveSession.Attendees.filter(attendee => attendee.Email.toLowerCase() !== request.OrganizerEmail?.toLowerCase());
                const isNotChanged = attendees.length === attendeesFromStore.length && attendees.every(attendee => attendeesFromStore.some(a => a.Email.toLowerCase() === attendee.Email.toLowerCase()));
                if((!attendees.length && !attendeesFromStore.length) || isNotChanged) {
                  break;
                }
                setTimeout(() => {
                  handleUpdateAttendees(attendees, attendeesFromStore);
                }, 1000);
                break;
              }
            }
          }
        }
      );
    });
  };

  const openPlaceOrder = async (
    resource: ResourceEntity,
    typeOrderResource: ETypeOrderResource,
    requestSaveSession: ISavePlaceOrderSessionDataRequest,
    request: IOpenOrderRequest,
    requestCheckMoveOrder?: ICheckMoveOrderRequest,
    requestMeetingType?: IRequestMeetingType
  ) => {
    switch (typeOrderResource) {
      case ETypeOrderResource.MoveOrder:
        if (requestCheckMoveOrder) {
          const response = await checkResourceSuitableForMovingOrder(requestCheckMoveOrder);
          const notify = new PreSelectedResourceInfo({
            type: EPreSelectedResource.MoveOrder,
            resources: [resource],
            message: "",
            movingOrder: {
              movingOrderType: response.MovingOrderType,
              itemAvailable: response.ItemAvailable ?? true,
            },
          });
          addItem(notify);
        }
        break;
      default:
        openOrder(resource, requestSaveSession, request, requestMeetingType);
        break;
    }
  };

  return {
    openPlaceOrder,
  };
};
